import { AgentAvatar } from '@/components/manager/agent-avatar'
import type { ChatPersona } from '@/lib/chat-persona'
import { AttachmentList, collectAttachments } from './attachment-list'
import { memo, useMemo, useEffect, useRef, useState, useCallback, type ReactNode, type ReactElement, type ComponentProps, type MouseEvent as ReactMouseEvent } from 'react'
import { ArrowRight, BookOpen, Brain, Check, Copy, Eye, Loader2, MessageSquare, MoreVertical, Pencil, RotateCcw, X } from 'lucide-react'
import { AssistantMarkdown } from './assistant-markdown'
import {
  Message as AIMessage,
  MessageAction,
  MessageActions,
  MessageContent as AIMessageContent,
} from '@/components/ai-elements/message'
import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { useConfirmDialog } from '@/components/ui/confirm-dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { useIsMobile } from '@/hooks/useIsMobile'
import type { SessionReasoningEffort } from '@/lib/session-chat-selection'
import { FileIcon, FolderIcon } from '@/components/icons/file-icons'
import { Reasoning } from './reasoning'
import { createUserMessageEditSubmission } from './message-edit'
import { PromptInput, type PromptInputHandle } from './prompt-input'
import { AgentToolCallWrapper, SubAgentAuthProvider, ToolCallGroup, formatElapsedDuration, shouldRenderToolCall, type ToolCallInfo } from './tool-call-card'
import { AssistantBody, shouldUseAgentToolCallWrapper } from './assistant-body'
import { LlmContextFlowDialog } from './llm-context-flow-dialog'
import { getCachedContextFlow, fetchContextFlow } from '@/lib/context-flow-cache'
import type { ChatAttachment, LlmContextFlow, MessageSegment, SessionInfo } from '@/hooks/useChat'
import type { ProviderId, RuntimeMode } from '@/lib/agents-api'
import type { ChatMode } from './mode-selector'
import type { SendTarget } from './send-target-selector'
import type { ReferencedFile } from './prompt-input'
import { getInjectedMemoryProvenanceEntries, type MemoryProvenanceSource } from '@/lib/memory-provenance'
import { getMemoryFeedbackLabel, type MemoryFeedbackKind } from '@/lib/memory-feedback'
import type { ResponseStyle } from '@jait/shared'
import { hasRenderableUserMessageContent } from './message-render-state'
import {
  getMobileUserMessageEditTop,
  getUserMessageEditComposerShellClassName,
  getUserMessageEditComposerTransitionClassName,
} from './message-edit-layout'
import { getMobileMessageActionsPositionClassName } from './message-mobile-actions'
import { shouldStartUserMessageEdit } from './message-bubble-click'
import {
  JAIT_REF_MIME,
  buildFallbackUserMessageSegments,
  formatLineRange,
  parseLegacyReferencedFilesBlock,
  userMessageTextFromSegments,
  userReferencedFilesFromSegments,
  serializeUserMessageSegmentsForClipboard,
  serializeUserMessageSegmentsToMarkdown,
  type UserMessageSegment,
} from '@/lib/user-message-segments'
import { TooltipHint } from '@/components/ui/tooltip'

interface MessageProps {
  persona?: ChatPersona
  messageId?: string
  messageIndex?: number
  messageFromEnd?: number
  role: 'user' | 'assistant'
  /** Visible system notice (e.g. a background terminal command finishing) — rendered as a small gray line. */
  kind?: 'system-notice'
  content: string
  /** Injected into a running agent turn via steering rather than sent as a normal turn. */
  steered?: boolean
  contextFlow?: LlmContextFlow
  /** Lightweight badge: a contextFlow payload exists on the server (lazy-loaded). */
  hasContextFlow?: boolean
  /** Lightweight badge: injected memories exist (lazy-loaded via contextFlow). */
  hasMemoryProvenance?: boolean
  /** Session ID + auth token, used to lazy-load contextFlow on demand. */
  sessionId?: string | null
  authToken?: string | null
  /** Clean display text (without appended file contents). Falls back to parsing content. */
  displayContent?: string
  /** Files the user referenced via @ chips — rendered as inline badges. */
  referencedFiles?: { path: string; name: string }[]
  /** Ordered text/file segments for consistent rendering and editing. */
  displaySegments?: UserMessageSegment[]
  attachments?: { name: string; mimeType: string; data: string; preview?: string }[]
  thinking?: string
  thinkingDuration?: number
  toolCalls?: ToolCallInfo[]
  /** Ordered interleaving of text and tool-call groups (from live streaming). */
  segments?: MessageSegment[]
  isStreaming?: boolean
  compact?: boolean
  preferLlmUi?: boolean
  /** Active chat provider. Non-Jait providers still use inline tool groups. */
  provider?: ProviderId
  threadControlThreads?: Record<string, unknown>[]
  onOpenTerminal?: (terminalId: string | null) => void
  renderInlineSecretPrompt?: (call: ToolCallInfo) => ReactNode
  onApprovalResponse?: (requestId: string, approved: boolean) => Promise<void> | void
  onEditMessage?: (
    messageId: string,
    newContent: string,
    messageIndex?: number,
    messageFromEnd?: number,
    metadata?: {
      referencedFiles?: { path: string; name: string }[]
      displaySegments?: UserMessageSegment[]
      /** Raw, unedited content of this message, for server-side target verification. */
      originalContent?: string
    },
  ) => Promise<boolean | void> | boolean | void
  /** Controlled edit ownership used to hide the normal mobile composer. */
  editing?: boolean
  onEditingChange?: (messageId: string, editing: boolean) => void
  editComposer?: {
    onVoiceInput?: () => void
    voiceRecording?: boolean
    voiceLevels?: number[]
    voiceTranscribing?: boolean
    onVoiceStop?: () => void
    mode?: ChatMode
    onModeChange?: (mode: ChatMode) => void
    sendTarget?: SendTarget
    onSendTargetChange?: (target: SendTarget) => void
    provider?: ProviderId
    onProviderChange?: (provider: ProviderId) => void
    responseStyle?: ResponseStyle
    onResponseStyleChange?: (style: ResponseStyle) => void
    providerRuntimeMode?: RuntimeMode
    onProviderRuntimeModeChange?: (mode: RuntimeMode) => void
    cliModel?: string | null
    onCliModelChange?: (model: string | null) => void
    reasoningEffort?: SessionReasoningEffort | null
    onReasoningEffortChange?: (reasoningEffort: SessionReasoningEffort | null) => void
    sessionInfo?: SessionInfo | null
    projectNodeId?: string
    availableFiles?: ReferencedFile[]
    onSearchFiles?: (query: string, limit: number, signal?: AbortSignal) => Promise<ReferencedFile[]>
    projectOpen?: boolean
  }
  onOpenPath?: (path: string, line?: number, column?: number) => Promise<void> | void
  onOpenDiff?: (filePath: string) => void
  onOpenMemorySource?: (source: MemoryProvenanceSource) => void
  onMemoryFeedback?: (feedback: {
    messageId: string
    kind: MemoryFeedbackKind
    content: string
  }) => Promise<void> | void
}

function ThinkingDots() {
  return (
    <span className="inline-flex items-center gap-1">
      {[0, 1, 2].map((index) => (
        <span
          key={index}
          className="h-1.5 w-1.5 rounded-full bg-current animate-pulse"
          style={{ animationDelay: `${index * 160}ms` }}
        />
      ))}
    </span>
  )
}

function MessageInner({
  persona,
  messageId,
  messageIndex,
  messageFromEnd,
  role,
  kind,
  content,
  steered,
  contextFlow,
  hasContextFlow,
  hasMemoryProvenance,
  sessionId,
  authToken,
  displayContent: displayContentProp,
  referencedFiles: referencedFilesProp,
  displaySegments: displaySegmentsProp,
  attachments: attachmentsProp,
  thinking,
  thinkingDuration,
  toolCalls: toolCallsProp,
  segments: segmentsProp,
  isStreaming,
  compact,
  preferLlmUi,
  provider: requestedProvider,
  threadControlThreads,
  onOpenTerminal,
  renderInlineSecretPrompt,
  onApprovalResponse,
  onEditMessage,
  editing: controlledEditing,
  onEditingChange,
  editComposer,
  onOpenPath,
  onOpenDiff,
  onOpenMemorySource,
  onMemoryFeedback,
}: MessageProps) {
  const provider = (persona?.providerId as ProviderId | undefined) ?? requestedProvider
  const isUser = role === 'user'
  const confirm = useConfirmDialog()

  // ── Defensive normalization ──────────────────────────────────────────
  // Upstream message hydration (esp. legacy/persisted sessions) can deliver
  // these as non-array values. Several render paths call .filter/.map/.some on
  // them, which throws "h.filter is not a function". Coerce to arrays once.
  const segments = Array.isArray(segmentsProp) ? segmentsProp : []
  const toolCalls = Array.isArray(toolCallsProp) ? toolCallsProp.filter(shouldRenderToolCall) : []

  // ── Lazy-loaded contextFlow ──────────────────────────────────────────
  // Snapshots only carry lightweight `hasContextFlow` / `hasMemoryProvenance`
  // badges. The (potentially multi-MB) full payload is fetched on demand when
  // the user opens the LLM-context dialog or the memory-provenance popover.
  // Fetched payloads live in a shared, module-level cache so that reopening
  // the trace (or scrolling a virtualized message back into view) does not
  // re-fetch the payload every time.
  const [lazyContextFlow, setLazyContextFlow] = useState<LlmContextFlow | null>(
    () => (typeof messageIndex === 'number' && sessionId)
      ? (getCachedContextFlow(sessionId, messageIndex) ?? null)
      : null,
  )
  const [contextFlowLoading, setContextFlowLoading] = useState(false)
  const effectiveContextFlow = contextFlow ?? lazyContextFlow ?? undefined
  const canLazyLoadContextFlow = !contextFlow && (hasContextFlow === true || hasMemoryProvenance === true)

  const loadContextFlow = useCallback(async () => {
    if (lazyContextFlow || contextFlowLoading || contextFlow) return
    if (typeof messageIndex !== 'number' || !sessionId) return
    setContextFlowLoading(true)
    try {
      const flow = await fetchContextFlow(sessionId, messageIndex, authToken ?? undefined)
      if (flow) setLazyContextFlow(flow)
    } catch {
      /* ignore — dialog just won't show data */
    } finally {
      setContextFlowLoading(false)
    }
  }, [lazyContextFlow, contextFlowLoading, contextFlow, messageIndex, sessionId, authToken])

  const { userDisplayText, userDisplaySegments } = useMemo(() => {
    if (!isUser) {
      return {
        userDisplayText: content,
        userDisplaySegments: [] as UserMessageSegment[],
      }
    }

    if (displaySegmentsProp?.length) {
      return {
        userDisplayText: userMessageTextFromSegments(displaySegmentsProp),
        userDisplaySegments: displaySegmentsProp,
      }
    }

    if (displayContentProp) {
      const fallbackSegments = buildFallbackUserMessageSegments(displayContentProp, referencedFilesProp)
      return { userDisplayText: displayContentProp, userDisplaySegments: fallbackSegments }
    }

    const parsed = parseLegacyReferencedFilesBlock(content)
    return { userDisplayText: parsed.text, userDisplaySegments: parsed.displaySegments }
  }, [isUser, content, displayContentProp, referencedFilesProp, displaySegmentsProp])

  const [copied, setCopied] = useState(false)
  const [isRetrying, setIsRetrying] = useState(false)
  const [localEditing, setLocalEditing] = useState(false)
  const isEditing = controlledEditing ?? localEditing
  const setEditing = useCallback((editing: boolean) => {
    setLocalEditing(editing)
    if (messageId) onEditingChange?.(messageId, editing)
  }, [messageId, onEditingChange])
  const [showEditComposer, setShowEditComposer] = useState(false)
  const [isSavingEdit, setIsSavingEdit] = useState(false)
  const [contextOpen, setContextOpen] = useState(false)
  const [memoryExpanded, setMemoryExpanded] = useState(false)
  const [memoryFeedbackState, setMemoryFeedbackState] = useState<{
    kind: MemoryFeedbackKind
    status: 'saving' | 'saved'
  } | null>(null)
  const [editDraft, setEditDraft] = useState('')
  const [editSegments, setEditSegments] = useState<UserMessageSegment[]>([])
  const [optimisticUserDisplayText, setOptimisticUserDisplayText] = useState<string | null>(null)
  const [optimisticUserDisplaySegments, setOptimisticUserDisplaySegments] = useState<UserMessageSegment[] | null>(null)
  const userAttachments = useMemo(() => {
    if (!isUser) return []
    return collectAttachments(attachmentsProp, optimisticUserDisplaySegments ?? userDisplaySegments)
  }, [attachmentsProp, isUser, optimisticUserDisplaySegments, userDisplaySegments])
  const hasUserRenderableContent = isUser && hasRenderableUserMessageContent({
    content,
    userDisplayText: optimisticUserDisplayText ?? userDisplayText,
    userDisplaySegments: optimisticUserDisplaySegments ?? userDisplaySegments,
    attachmentCount: userAttachments.length,
  })
  const copyTimerRef = useRef<number | null>(null)
  const memoryFeedbackTimerRef = useRef<number | null>(null)
  const userBubbleRef = useRef<HTMLDivElement | null>(null)
  const editPromptInputRef = useRef<PromptInputHandle | null>(null)
  // Center of the visual viewport (the area above the on-screen keyboard).
  const [mobileEditTop, setMobileEditTop] = useState<number | null>(null)

  useEffect(() => {
    return () => {
      if (copyTimerRef.current !== null) window.clearTimeout(copyTimerRef.current)
      if (memoryFeedbackTimerRef.current !== null) window.clearTimeout(memoryFeedbackTimerRef.current)
    }
  }, [])

  // Tracks the last value we synced into the edit draft. `userDisplaySegments`
  // can be a freshly-allocated array each render (e.g. derived/legacy fallback
  // paths), so writing it back to state unconditionally would re-trigger this
  // effect forever — React error #185 "Maximum update depth exceeded". We only
  // write state when the *content* actually changed.
  const lastEditSyncRef = useRef<{ text: string; segmentsKey: string }>({ text: '', segmentsKey: '' })
  useEffect(() => {
    if (isEditing) return
    const segmentsKey = JSON.stringify(userDisplaySegments)
    if (lastEditSyncRef.current.text === userDisplayText && lastEditSyncRef.current.segmentsKey === segmentsKey) return
    lastEditSyncRef.current = { text: userDisplayText, segmentsKey }
    setEditDraft(userDisplayText)
    setEditSegments(userDisplaySegments)
  }, [isEditing, userDisplaySegments, userDisplayText])

  useEffect(() => {
    if (!optimisticUserDisplaySegments && optimisticUserDisplayText == null) return
    const matchesText = optimisticUserDisplayText === userDisplayText
    const matchesSegments = optimisticUserDisplaySegments === userDisplaySegments
      || JSON.stringify(optimisticUserDisplaySegments ?? []) === JSON.stringify(userDisplaySegments)
    if (matchesText && matchesSegments) {
      setOptimisticUserDisplayText(null)
      setOptimisticUserDisplaySegments(null)
    }
  }, [optimisticUserDisplaySegments, optimisticUserDisplayText, userDisplaySegments, userDisplayText])

  useEffect(() => {
    if (!isEditing) {
      setShowEditComposer(false)
      return
    }
    const frameId = window.requestAnimationFrame(() => {
      setShowEditComposer(true)
    })
    editPromptInputRef.current?.focus()
    return () => window.cancelAnimationFrame(frameId)
  }, [isEditing])

  const buildUserCopyText = (): string => (
    serializeUserMessageSegmentsToMarkdown(userDisplaySegments) || userDisplayText
  )

  const buildCopyText = (): string => {
    const parts: string[] = []

    if (toolCalls && toolCalls.length > 0) {
      for (const call of toolCalls) {
        const label = call.tool.replace(/_/g, '.')
        const summary = Object.entries(call.args)
          .filter(([, v]) => v != null && v !== '')
          .map(([k, v]) => `${k}: ${typeof v === 'string' ? v : JSON.stringify(v)}`)
          .join(', ')
        const status = call.status === 'success' ? '\u2713' : call.status === 'error' ? '\u2717' : '\u2026'
        const duration =
          call.completedAt && call.startedAt
            ? formatElapsedDuration(call.startedAt, call.completedAt)
            : ''

        parts.push(`[${status} ${label}] ${summary}`)
        if (call.result?.message) {
          const msg =
            call.result.message.length > 500
              ? call.result.message.slice(0, 500) + '\u2026'
              : call.result.message
          parts.push(msg)
        }
        if (duration) parts.push(`Duration: ${duration}`)
        parts.push('')
      }
    }

    const messageText = isUser ? buildUserCopyText() : content
    if (messageText) {
      parts.push(messageText)
    }

    return parts.join('\n').trim()
  }

  const copyToClipboard = async () => {
    const text = buildCopyText()
    if (!text) return
    const clipboardPayload = isUser ? serializeUserMessageSegmentsForClipboard(userDisplaySegments) : null

    try {
      if (clipboardPayload && typeof ClipboardItem !== 'undefined' && navigator.clipboard?.write) {
        try {
          await navigator.clipboard.write([
            new ClipboardItem({
              'text/plain': new Blob([text], { type: 'text/plain' }),
              [JAIT_REF_MIME]: new Blob([clipboardPayload], { type: JAIT_REF_MIME }),
            }),
          ])
        } catch {
          if (!navigator.clipboard?.writeText) throw new Error('Clipboard text API unavailable')
          await navigator.clipboard.writeText(text)
        }
      } else if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(text)
      } else {
        throw new Error('Clipboard API unavailable')
      }
    } catch {
      const textArea = document.createElement('textarea')
      textArea.value = text
      textArea.style.position = 'fixed'
      textArea.style.opacity = '0'
      document.body.appendChild(textArea)
      textArea.focus()
      textArea.select()
      document.execCommand('copy')
      document.body.removeChild(textArea)
    }

    setCopied(true)
    if (copyTimerRef.current !== null) window.clearTimeout(copyTimerRef.current)
    copyTimerRef.current = window.setTimeout(() => setCopied(false), 1200)
  }

  const isMobile = useIsMobile()

  // Center the edited user message in the visual viewport — the visible area
  // above the software keyboard — without scrolling the transcript underneath.
  useEffect(() => {
    if (!isEditing || !isMobile) {
      setMobileEditTop(null)
      return
    }
    const update = () => {
      setMobileEditTop(getMobileUserMessageEditTop(window.visualViewport, window.innerHeight))
    }
    update()
    window.visualViewport?.addEventListener?.('resize', update)
    window.visualViewport?.addEventListener?.('scroll', update)
    window.addEventListener('resize', update)
    return () => {
      window.visualViewport?.removeEventListener?.('resize', update)
      window.visualViewport?.removeEventListener?.('scroll', update)
      window.removeEventListener('resize', update)
    }
  }, [isEditing, isMobile])
  const canEdit = isUser && !!messageId && !!onEditMessage
  const canRetry = canEdit
  const showStreamingIndicator = isStreaming && !thinking && !(typeof content === 'string' ? content : '').trim()
  const hasCopyableMessage = buildCopyText().length > 0
  const canCopyMessage = (isUser || !isStreaming) && hasCopyableMessage
  const canSendMemoryFeedback = !isUser && !!messageId && !!onMemoryFeedback && !isStreaming
  const memoryFeedbackSaving = memoryFeedbackState?.status === 'saving'

  const startEditing = useCallback(() => {
    if (!canEdit || isSavingEdit) return
    setEditDraft(userDisplayText)
    setEditSegments(userDisplaySegments)
    setEditing(true)
  }, [canEdit, isSavingEdit, setEditing, userDisplaySegments, userDisplayText])

  const cancelEditing = useCallback(() => {
    setEditDraft(userDisplayText)
    setEditSegments(userDisplaySegments)
    setEditing(false)
  }, [setEditing, userDisplaySegments, userDisplayText])

  const handleUserBubbleClick = (event: ReactMouseEvent<HTMLElement>) => {
    const selection = typeof window !== 'undefined' ? window.getSelection()?.toString() : ''
    if (!shouldStartUserMessageEdit(event, selection, { canEdit, isEditing }, event.currentTarget)) return
    startEditing()
  }

  const saveEditedMessage = useCallback(async (nextText?: string, nextSegments?: UserMessageSegment[], nextAttachments?: ChatAttachment[]) => {
    if (!canEdit || !messageId || !onEditMessage || isSavingEdit) return
    const submission = createUserMessageEditSubmission(nextText ?? editDraft, nextSegments ?? editSegments, userDisplaySegments, nextAttachments)
    if (!submission) return

    setOptimisticUserDisplayText(submission.text)
    setOptimisticUserDisplaySegments(submission.displaySegments)
    setIsSavingEdit(true)
    try {
      const replaced = await onEditMessage(messageId, submission.text, messageIndex, messageFromEnd, {
        referencedFiles: submission.referencedFiles,
        displaySegments: submission.displaySegments,
        originalContent: content,
      })
      if (replaced === false) {
        setOptimisticUserDisplayText(null)
        setOptimisticUserDisplaySegments(null)
      } else {
        setEditing(false)
      }
    } catch (error) {
      setOptimisticUserDisplayText(null)
      setOptimisticUserDisplaySegments(null)
      throw error
    } finally {
      setIsSavingEdit(false)
    }
  }, [
    canEdit,
    content,
    editDraft,
    editSegments,
    isSavingEdit,
    messageId,
    messageFromEnd,
    messageIndex,
    onEditMessage,
    setEditing,
    userDisplaySegments,
  ])

  const sendFromMessage = async (nextContent: string, nextSegments?: UserMessageSegment[]) => {
    if (!canEdit || !messageId || !onEditMessage) return
    const next = nextContent.trim()
    if (!next) return
    await onEditMessage(messageId, next, messageIndex, messageFromEnd, {
      referencedFiles: (nextSegments ?? userDisplaySegments)
        .filter((segment): segment is Extract<UserMessageSegment, { type: 'file' }> => segment.type === 'file')
        .map((segment) => ({ path: segment.path, name: segment.name })),
      displaySegments: nextSegments ?? userDisplaySegments,
      originalContent: content,
    })
  }

  const handleRetryFromHere = async () => {
    if (!canRetry) return
    setIsRetrying(true)
    try {
      await sendFromMessage(userDisplayText, userDisplaySegments)
    } finally {
      setIsRetrying(false)
    }
  }

  const handleMemoryFeedback = async (kind: MemoryFeedbackKind) => {
    if (!canSendMemoryFeedback || !messageId || memoryFeedbackSaving) return
    if (kind === 'should_have_remembered') {
      const accepted = await confirm({
        title: 'Memorize this?',
        description: 'This saves a memory feedback item with this assistant message as context. Future agents can use it when reviewing or updating memory for this chat and project.',
        confirmLabel: 'Memorize',
        cancelLabel: 'Cancel',
      })
      if (!accepted) return
    }
    setMemoryFeedbackState({ kind, status: 'saving' })
    try {
      await onMemoryFeedback?.({ messageId, kind, content })
      setMemoryFeedbackState({ kind, status: 'saved' })
      if (memoryFeedbackTimerRef.current !== null) window.clearTimeout(memoryFeedbackTimerRef.current)
      memoryFeedbackTimerRef.current = window.setTimeout(() => setMemoryFeedbackState(null), 1200)
    } catch {
      setMemoryFeedbackState(null)
    }
  }

  const renderMemoryFeedbackIcon = (kind: MemoryFeedbackKind, fallback: ReactElement): ReactElement => {
    if (memoryFeedbackState?.kind !== kind) return fallback
    if (memoryFeedbackState.status === 'saving') return <Loader2 className="h-3.5 w-3.5 animate-spin" />
    return <Check className="h-3.5 w-3.5" />
  }

  const messageActionItems: {
    key: string
    label: string
    icon: ReactElement
    onClick: () => void
    disabled?: boolean
  }[] = [
    ...(canEdit ? [{
      key: 'edit',
      label: 'Edit message',
      icon: <Pencil className="h-3.5 w-3.5" />,
      onClick: () => startEditing(),
    }] : []),
    ...(canRetry ? [{
      key: 'retry',
      label: 'Retry from this message',
      icon: <RotateCcw className={cn('h-3.5 w-3.5', isRetrying && 'animate-spin')} />,
      onClick: () => { void handleRetryFromHere() },
      disabled: isRetrying,
    }] : []),
    ...(!isUser && (effectiveContextFlow || canLazyLoadContextFlow) ? [{
      key: 'context',
      label: 'View LLM context',
      icon: contextFlowLoading
        ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
        : <Eye className="h-3.5 w-3.5" />,
      onClick: () => {
        if (!effectiveContextFlow) void loadContextFlow()
        setContextOpen(true)
      },
    }] : []),
    ...(canSendMemoryFeedback ? [{
      key: 'memory',
      label: getMemoryFeedbackLabel('should_have_remembered'),
      icon: renderMemoryFeedbackIcon('should_have_remembered', <Brain className="h-3.5 w-3.5" />),
      onClick: () => { void handleMemoryFeedback('should_have_remembered') },
      disabled: memoryFeedbackSaving,
    }] : []),
    {
      key: 'copy',
      label: copied ? 'Copied' : 'Copy message',
      icon: copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />,
      onClick: () => { void copyToClipboard() },
    },
  ]

  const renderActions = (outsideBubble?: boolean) => {
    if (isEditing || !canCopyMessage || messageActionItems.length === 0) return null

    // On touch devices, collapse the always-visible action row into a single
    // 3-dot menu anchored bottom-right (accessibility / avoids clutter).
    if (isMobile) {
      return (
        <div
          className={cn(
            'absolute z-10',
            getMobileMessageActionsPositionClassName(isUser, outsideBubble),
          )}
        >
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                type="button"
                aria-label="Message actions"
                className="relative flex h-6 w-6 items-center justify-center text-muted-foreground transition-colors active:text-foreground"
              >
                {/* Pinned to the tap-area's right edge. The lucide
                    ellipsis-vertical glyph is inset 7.33px (at 16px scale,
                    viewBox 24) from its box's right edge but only 2.67px from
                    its box's bottom (bottom dot r=1 at cy=19), so the box is
                    lifted 4.67px (= 7.33 - 2.67) — that makes the visible gap
                    to the bubble edge pixel-identical bottom vs right. */}
                <MoreVertical className="absolute bottom-[4.67px] right-0 h-4 w-4" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" side="top" className="min-w-[10rem]">
              {messageActionItems.map((item) => (
                <DropdownMenuItem
                  key={item.key}
                  disabled={item.disabled}
                  onSelect={(e) => {
                    e.preventDefault()
                    item.onClick()
                  }}
                  className="gap-2"
                >
                  {item.icon as ComponentProps<typeof DropdownMenuItem>['children']}
                  <span>{item.label}</span>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )
    }

    return (
      <div
        className={cn(
          'absolute z-10 rounded-md border border-border/60 bg-background/95 p-0.5 shadow-sm',
          outsideBubble ? 'right-0 top-full mt-0.5' : 'right-1 bottom-[.25rem]',
          'opacity-0 transition-opacity group-hover/message:opacity-100 focus-within:opacity-100',
          copied && 'opacity-100',
        )}
      >
        <MessageActions>
          {messageActionItems.map((item) => (
            <MessageAction
              key={item.key}
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                item.onClick()
              }}
              disabled={item.disabled}
              aria-label={item.label}
              tooltip={item.label}
              className="h-5 w-5 rounded-md p-0 [&_svg]:h-3 [&_svg]:w-3"
            >
              {item.icon}
            </MessageAction>
          ))}
        </MessageActions>
      </div>
    )
  }

  const assistantActions = !isUser ? renderActions() : null
  const hasThinkingSegment = !isUser && segments?.some((segment) => segment.type === 'thinking' && segment.content.trim())
  const hasSteeringSegment = !isUser && segments?.some((segment) => segment.type === 'steering')
  const memoryProvenanceEntries = !isUser ? getInjectedMemoryProvenanceEntries(effectiveContextFlow) : []
  const showMemoryBadge = !isUser && (memoryProvenanceEntries.length > 0 || (hasMemoryProvenance && !effectiveContextFlow))
  const memoryProvenance = showMemoryBadge ? (
    <div className="not-prose mt-2 flex min-w-0 flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
      <TooltipHint content={memoryExpanded ? 'Hide injected memories' : 'Show memories injected for this answer'}>
      <button
        type="button"
        className="inline-flex max-w-full items-center gap-1.5 rounded-md border border-amber-500/30 bg-amber-500/10 px-2 py-1 font-medium text-amber-700 transition-colors hover:bg-amber-500/15 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 dark:text-amber-200"
        onClick={(event) => {
          event.preventDefault()
          event.stopPropagation()
          if (!effectiveContextFlow && hasMemoryProvenance) void loadContextFlow()
          setMemoryExpanded((open) => !open)
        }}
        aria-expanded={memoryExpanded}
      >
        <Brain className="h-3.5 w-3.5 shrink-0" />
        <span>Memory</span>
        {memoryProvenanceEntries.length > 0
          ? <span className="rounded bg-background/75 px-1 text-2xs">{memoryProvenanceEntries.length}</span>
          : (contextFlowLoading
              ? <Loader2 className="h-3 w-3 animate-spin" />
              : <span className="rounded bg-background/75 px-1 text-2xs">•</span>)}
      </button>
      </TooltipHint>
      {memoryExpanded && memoryProvenanceEntries.slice(0, 3).map((entry) => {
        const source: MemoryProvenanceSource = {
          memoryId: entry.id,
          sourceType: entry.sourceType,
          sourceId: entry.sourceId,
          sourceSurface: entry.sourceSurface,
        }
        const canOpenSource = Boolean(onOpenMemorySource && entry.sourceSurface === 'chat' && entry.sourceId)
        return (
          <TooltipHint key={entry.id} content={canOpenSource
              ? `Open source chat for injected memory: ${entry.source || entry.id}`
              : `View injected memory source: ${entry.source || entry.id}`}>
          <button
            type="button"
            className="inline-flex max-w-[220px] items-center gap-1.5 rounded-md border border-border/70 bg-muted/45 px-2 py-1 text-foreground transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
            onClick={(event) => {
              event.preventDefault()
              event.stopPropagation()
              if (canOpenSource) onOpenMemorySource?.(source)
              else setContextOpen(true)
            }}
          >
            {canOpenSource ? <MessageSquare className="h-3.5 w-3.5 shrink-0" /> : <Brain className="h-3.5 w-3.5 shrink-0" />}
            <span className="truncate">{entry.source || entry.id}</span>
          </button>
          </TooltipHint>
        )
      })}
    </div>
  ) : null

  // Visible system notices (e.g. a background terminal command finishing) are
  // rendered as a small right-aligned gray line, not a full message bubble.
  if (kind === 'system-notice') {
    return (
      <div className="flex w-full justify-end py-1">
        <span className="min-w-0 max-w-[85%] select-text break-words text-right text-[11px] leading-relaxed text-muted-foreground/70 [overflow-wrap:anywhere]">
          {content}
        </span>
      </div>
    )
  }

  return (
    <>
    <div className="w-full">
    <AIMessage from={role} className={cn(compact ? 'py-2' : 'py-4')}>
      <div className={cn(
        'min-w-0 space-y-2',
        hasSteeringSegment ? 'w-full max-w-full' : 'max-w-[85%]',
        isUser && 'order-1',
      )}>
        {!isUser && persona && <div data-persona-agent-id={persona.id} className="flex items-center gap-2 text-xs text-muted-foreground">
          <AgentAvatar avatar={persona.avatar} className="h-8 w-8" />
          <span className="font-medium text-foreground">{persona.name}</span>
          <span>{persona.providerId} · {persona.model ?? 'Default model'}</span>
        </div>}
        {!isUser && thinking && !hasThinkingSegment && (
          <Reasoning
            content={thinking}
            isStreaming={!!isStreaming && !content}
            duration={thinkingDuration}
            onOpenPath={onOpenPath}
          />
        )}

        {!isUser && segments && segments.length > 0 ? (
          <SubAgentAuthProvider sessionId={sessionId} authToken={authToken}>
            <div className="relative min-w-0 max-w-full select-text break-words [overflow-wrap:anywhere]">
              <AssistantBody
                segments={segments}
                toolCalls={toolCalls}
                isStreaming={isStreaming}
                hasStreamingText={!!content}
                thinkingDuration={thinkingDuration}
                provider={provider}
                threadControlThreads={threadControlThreads}
                onOpenTerminal={onOpenTerminal}
                onOpenDiff={onOpenDiff}
                renderInlineSecretPrompt={renderInlineSecretPrompt}
                onApprovalResponse={onApprovalResponse}
                compact={compact}
                preferLlmUi={preferLlmUi}
                onOpenPath={onOpenPath}
                capNonSteeringWidth={hasSteeringSegment}
              />

              <div className={cn('min-w-0', hasSteeringSegment && 'max-w-[85%]')}>
                {assistantActions}
                {memoryProvenance}
              </div>
            </div>
          </SubAgentAuthProvider>
        ) : (
          <>
            {toolCalls && toolCalls.length > 0 && (
              <SubAgentAuthProvider sessionId={sessionId} authToken={authToken}>
                {shouldUseAgentToolCallWrapper(provider, toolCalls) ? (
                  <AgentToolCallWrapper
                    provider={provider}
                    calls={toolCalls}
                    isStreaming={isStreaming}
                    threadControlThreads={threadControlThreads}
                    onOpenTerminal={onOpenTerminal}
                    onOpenDiff={onOpenDiff}
                    renderInlineSecretPrompt={renderInlineSecretPrompt}
                    onApprovalResponse={onApprovalResponse}
                  />
                ) : (
                  <ToolCallGroup
                    calls={toolCalls}
                    collapsible
                    threadControlThreads={threadControlThreads}
                    onOpenTerminal={onOpenTerminal}
                    onOpenDiff={onOpenDiff}
                    renderInlineSecretPrompt={renderInlineSecretPrompt}
                    onApprovalResponse={onApprovalResponse}
                  />
                )}
              </SubAgentAuthProvider>
            )}

            {hasUserRenderableContent || content ? (
              isUser ? (
                isEditing ? (
                  <div
                    className={getUserMessageEditComposerShellClassName()}
                    style={
                      isMobile && mobileEditTop != null
                        ? { top: mobileEditTop }
                        : undefined
                    }
                    onClick={(event) => event.stopPropagation()}
                  >
                    <div
                      className={getUserMessageEditComposerTransitionClassName(showEditComposer)}
                    >
                        <PromptInput
                          key={`edit-${messageId ?? 'user-message'}`}
                          ref={editPromptInputRef}
                          draftStateKey={`edit:${messageId ?? 'user-message'}`}
                          value={editDraft}
                          segments={editSegments}
                          initialAttachments={userAttachments}
                          onChange={(nextValue) => {
                            setEditDraft(nextValue)
                            setEditSegments(editPromptInputRef.current?.getSegments() ?? [])
                          }}
                          onSubmit={(_chipFiles, nextAttachments, nextSegments) => {
                            const freshSegments = nextSegments ?? []
                            const freshText = userMessageTextFromSegments(freshSegments) || editDraft
                            setEditSegments(freshSegments)
                            void saveEditedMessage(freshText, freshSegments, nextAttachments)
                          }}
                          disabled={isSavingEdit}
                          controlsDisabled={isSavingEdit}
                          placeholder="Edit message..."
                          onVoiceInput={editComposer?.onVoiceInput}
                          voiceRecording={editComposer?.voiceRecording}
                          voiceLevels={editComposer?.voiceLevels}
                          voiceTranscribing={editComposer?.voiceTranscribing}
                          onVoiceStop={editComposer?.onVoiceStop}
                          mode={editComposer?.mode}
                          onModeChange={editComposer?.onModeChange}
                          sendTarget={editComposer?.sendTarget}
                          onSendTargetChange={editComposer?.onSendTargetChange}
                          provider={editComposer?.provider}
                          onProviderChange={editComposer?.onProviderChange}
                          responseStyle={editComposer?.responseStyle}
                          onResponseStyleChange={editComposer?.onResponseStyleChange}
                          providerRuntimeMode={editComposer?.providerRuntimeMode}
                          onProviderRuntimeModeChange={editComposer?.onProviderRuntimeModeChange}
                          cliModel={editComposer?.cliModel}
                          onCliModelChange={editComposer?.onCliModelChange}
                          reasoningEffort={editComposer?.reasoningEffort}
                          onReasoningEffortChange={editComposer?.onReasoningEffortChange}
                          sessionInfo={editComposer?.sessionInfo}
                          projectNodeId={editComposer?.projectNodeId}
                          availableFiles={editComposer?.availableFiles ?? userReferencedFilesFromSegments(userDisplaySegments)}
                          onSearchFiles={editComposer?.onSearchFiles}
                          projectOpen={editComposer?.projectOpen ?? userReferencedFilesFromSegments(userDisplaySegments).length > 0}
                          footerTrailingContent={(
                            <TooltipHint content="Cancel editing">
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 shrink-0 rounded-lg"
                              onClick={cancelEditing}
                              disabled={isSavingEdit}
                              aria-label="Cancel editing message"
                            >
                              <X className="h-3.5 w-3.5" />
                            </Button>
                            </TooltipHint>
                          )}
                          className="rounded-lg border-primary/20 bg-primary/[0.08] dark:!bg-primary/[0.08] shadow-none [&_.text-muted-foreground]:text-muted-foreground [&>div]:!bg-transparent [&_[contenteditable='true']]:!bg-transparent"
                        />
                    </div>
                  </div>
                ) : (
                  <div className="relative w-fit max-w-full">
                    {steered && (
                      <span className="mb-1 inline-flex items-center gap-1 text-2xs font-medium uppercase tracking-wider text-primary/70">
                        <ArrowRight className="h-3 w-3" />
                        Steered into running turn
                      </span>
                    )}
                    <TooltipHint content={canEdit && !isEditing ? 'Click to edit message' : undefined}>
                    <AIMessageContent
                      ref={userBubbleRef}
                      data-message-from="user"
                      className={cn(
                        'min-w-0 rounded-lg bg-muted p-4 break-words [overflow-wrap:anywhere]',
                        canEdit && !isEditing && 'cursor-text transition-colors hover:bg-muted/80',
                        'leading-relaxed [font-size:0.9rem]',
                        steered && 'border border-dashed border-primary/40 bg-primary/5',
                      )}
                      onClick={handleUserBubbleClick}
                    >
                      <div className="min-w-0 space-y-3">
                        <AttachmentList attachments={userAttachments} />
                        <div className="min-w-0 whitespace-pre-wrap break-words [overflow-wrap:anywhere]">
                          {(optimisticUserDisplaySegments ?? userDisplaySegments).length > 0
                            ? (optimisticUserDisplaySegments ?? userDisplaySegments).map((segment, index) =>
                                segment.type === 'text' ? (
                                  <span key={`text-${index}`}>{segment.text}</span>
                                ) : segment.type === 'file' ? (
                                  <TooltipHint key={`${segment.path}-${index}`} content={segment.path}>
                                  <span
                                    className="mx-[2px] inline-flex items-center gap-1.5 rounded-md border border-border/70 bg-muted/45 px-2 py-1 text-xs font-medium leading-none text-foreground align-middle select-none"
                                  >
                                    <FileIcon filename={segment.name} className="h-3.5 w-3.5 shrink-0" />
                                    <span className="max-w-[180px] truncate">{segment.lineRange ? `${segment.name}:${formatLineRange(segment.lineRange).replace(/^lines? /, '')}` : segment.name}</span>
                                  </span>
                                  </TooltipHint>
                                ) : segment.type === 'project' ? (
                                  <TooltipHint key={`${segment.path}-${index}`} content={segment.path}>
                                  <span
                                    className="mx-[2px] inline-flex items-center gap-1.5 rounded-md border border-border/70 bg-muted/45 px-2 py-1 text-xs font-medium leading-none text-foreground align-middle select-none"
                                  >
                                    <FolderIcon name={segment.name} className="h-3.5 w-3.5 shrink-0" />
                                    <span className="max-w-[180px] truncate">{segment.name}</span>
                                  </span>
                                  </TooltipHint>
                                ) : segment.type === 'terminal' ? (
                                  <TooltipHint key={`${segment.terminalId}-${index}`} content={segment.projectRoot ? `${segment.terminalId} · ${segment.projectRoot}` : segment.terminalId}>
                                  <span
                                    className="mx-[2px] inline-flex items-center gap-1.5 rounded-md border border-border/70 bg-muted/45 px-2 py-1 text-xs font-medium leading-none text-foreground align-middle select-none"
                                  >
                                    <span className="inline-flex h-3.5 w-3.5 items-center justify-center rounded-sm bg-muted-foreground/15 text-2xs font-bold uppercase text-muted-foreground">T</span>
                                    <span className="max-w-[180px] truncate">{segment.lineRange ? `${segment.name}:${formatLineRange(segment.lineRange).replace(/^lines? /, '')}` : segment.name}</span>
                                  </span>
                                  </TooltipHint>
                                ) : segment.type === 'skill' ? (
                                  <TooltipHint key={`${segment.id}-${index}`} content={'/' + segment.id}>
                                  <span
                                    className="mx-[2px] inline-flex items-center gap-1.5 rounded-md border border-border/70 bg-muted/45 px-2 py-1 text-xs font-medium leading-none text-foreground align-middle select-none"
                                  >
                                    <BookOpen className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                                    <span className="max-w-[180px] truncate">/{segment.name}</span>
                                  </span>
                                  </TooltipHint>
                                ) : segment.type === 'chat' ? (
                                  <TooltipHint key={`chat-${segment.sessionId}-${index}`} content={`Chat: ${segment.sessionId}`}>
                                  <span
                                    className="mx-[2px] inline-flex items-center gap-1.5 rounded-md border border-border/70 bg-muted/45 px-2 py-1 text-xs font-medium leading-none text-foreground align-middle select-none"
                                  >
                                    <MessageSquare className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                                    <span className="max-w-[180px] truncate">{segment.name}</span>
                                  </span>
                                  </TooltipHint>
                                ) : null,
                              )
                            : (optimisticUserDisplayText ?? userDisplayText)}
                        </div>
                      </div>
                    </AIMessageContent>
                    </TooltipHint>

                    {renderActions()}
                  </div>
                )
              ) : (
                <div className="relative min-w-0 max-w-full break-words [overflow-wrap:anywhere]">
                  <AIMessageContent
                    data-message-from="assistant"
                    className="min-w-0 max-w-full break-words [overflow-wrap:anywhere]"
                  >
                    <AssistantMarkdown
                      content={content}
                      compact={compact}
                      isStreaming={isStreaming}
                      preferLlmUi={preferLlmUi}
                      onOpenPath={onOpenPath}
                    />
                  </AIMessageContent>
                  {assistantActions}
                  {memoryProvenance}
                </div>
              )
            ) : showStreamingIndicator ? (
              <div className="flex items-center gap-3 px-1 py-1 text-sm text-muted-foreground">
                <ThinkingDots />
              </div>
            ) : null}
          </>
        )}
      </div>
    </AIMessage>
    </div>
    <LlmContextFlowDialog
      open={contextOpen}
      onOpenChange={setContextOpen}
      contextFlow={effectiveContextFlow}
      responseContent={content}
      loading={contextFlowLoading}
    />
    </>
  )
}

export const Message = memo(MessageInner, (prev, next) => {
  // messageFromEnd / messageIndex only affect the edit callback, not rendering.
  // They change for every message when a new one is added, busting memo for the
  // entire list.  Skip them so only render-relevant props trigger re-renders.
  const { messageFromEnd: _a, messageIndex: _b, ...prevRest } = prev
  const { messageFromEnd: _c, messageIndex: _d, ...nextRest } = next
  const keys = Object.keys(nextRest) as (keyof typeof nextRest)[]
  return keys.length === Object.keys(prevRest).length && keys.every((k) => Object.is(prevRest[k], nextRest[k]))
})
Message.displayName = 'Message'
